import ConnectionClient from '../client/connection/ConnectionClient';
import GatewayContainer from '../config/GatewayContainer';
import GatewayNames from '../constant/GatewayNames';
import { CommonConnectionModel, ConnectionModel, MongoConnectionModel } from '../domain/connection/ConnectionDomains';
import MessageUtil from '../util/MessageUtil';
import LanguageUtil from '../util/LanguageUtil';
import React from 'react';
import { EnrichedComponentCategory } from '../domain/category/CategoryOperationDomains';
import ComponentName from '../constant/ComponentName';
import FolderClient from '../client/folder/FolderClient';
import { dispatch } from '../redux/store';
import { openBulkActionDialogFromDependency } from '../redux/actions/BulkActionActions';
import { query } from '@trlogic/client-js';
import { changeApiVariableDirty } from '../redux/actions/ApiVariableActions';
import { ActionType } from '../constant/Constants';
import SelectQuery = query.SelectQuery;
import { BulkActionDependencyError } from 'redux/reducers/BulkActionReducer';
import DeployErrorUtil from '../util/DeployErrorUtil';

class ConnectionService {
  private folderClient: FolderClient = GatewayContainer.getInstance().get(GatewayNames.FOLDER_CLIENT);
  private connectionClient: ConnectionClient = GatewayContainer.getInstance().get(GatewayNames.CONNECTION_CLIENT);

  public async saveConnection(connection: CommonConnectionModel, isUpdate: boolean, folder: EnrichedComponentCategory) {
    try {
      let saveConnectionModel: ConnectionModel;
      const isExist: boolean = await this.connectionClient.exist(connection.name);
      if (isExist && !isUpdate) {
        MessageUtil.alreadyExist('connection');
        return false;
      }
      if (this.connectionIsValid(connection)) {
        switch (connection.databaseType) {
          case 'Mongo':
            const mongoConnection: MongoConnectionModel = {
              stage: 'DRAFT',
              databaseName: connection.databaseName!,
              connectionString: connection.connectionString!,
              name: connection.name,
              databaseType: connection.databaseType,
              description: connection.description!,
              permission: connection.permission,
              collaborators: connection.collaborators,
              status: connection.status,
            };
            saveConnectionModel = mongoConnection;
            break;
          default:
            delete connection.dataCenter;
            delete connection.connectionString;
            saveConnectionModel = connection;
            break;
        }
        await this.connectionClient.save(saveConnectionModel);
        if (!isUpdate && folder != undefined) {
          await this.folderClient.addItemToCategory(ComponentName.CONNECTION, folder.id, connection.name);
        }
        MessageUtil.success({
          summary: 'Connection',
          detail: (
            <div>
              <b>{connection.name}</b> {LanguageUtil.get({ id: 'messages.saved', defaultMessage: 'saved' })}
            </div>
          ),
        });
        dispatch(changeApiVariableDirty(false));
        return true;
      } else {
        MessageUtil.error({
          summary: 'Please check connection fields.',
          detail: (
            <div data-testid={`${ComponentName.CONNECTION}_save_not_valid_error`}>
              <b>{connection.name}</b> {LanguageUtil.get({ id: 'messages.cannotSaved', defaultMessage: 'can not save' })}
            </div>
          ),
        });
        return false;
      }
    } catch (e) {
      MessageUtil.error({
        summary: 'Connection',
        detail: (
          <div>
            <b>{connection.name}</b> {LanguageUtil.get({ id: 'messages.cannotSaved', defaultMessage: 'not saved' })}
          </div>
        ),
      });
      console.error(e);
      return false;
    }
  }

  public async removeConnection(id: string) {
    try {
      const response = await this.connectionClient.remove(id);
      if (response.code) {
        return false;
      }
      MessageUtil.success({
        summary: 'Connection removed',
        detail: (
          <div>
            <b>{id}</b> {LanguageUtil.get({ id: 'messages.removed', defaultMessage: 'removed' })}
          </div>
        ),
      });
      return true;
    } catch (e: any) {
      if (DeployErrorUtil.show(e, LanguageUtil.get({ id: 'common.label.connection', defaultMessage: 'Connection' }))) {
        return false;
      }
      const dependencies: any = e.response.data;
      if (dependencies?.message == 'Dependency Exception') {
        dispatch(openBulkActionDialogFromDependency(dependencies, ComponentName.CONNECTION));
      }
      MessageUtil.error({
        summary: 'Connection can not be removed',
        detail: (
          <div>
            <b>{id}</b> {LanguageUtil.get({ id: 'messages.cannotSaved', defaultMessage: 'can not save' })}
          </div>
        ),
      });
      return false;
    }
  }

  public count(query: SelectQuery): Promise<number> {
    return this.connectionClient.count(query);
  }

  public async deployConnection(connection: CommonConnectionModel) {
    try {
      await this.connectionClient.deploy(connection.name);
      MessageUtil.success({
        summary: LanguageUtil.get({ id: 'common.label.connection', defaultMessage: 'Connection' }),
        detail: LanguageUtil.get({ id: 'messages.success.connectionDeploySuccess', defaultMessage: 'Connection deployed successfully' }),
      });
    } catch (e) {
      if (DeployErrorUtil.show(e, LanguageUtil.get({ id: 'common.label.connection', defaultMessage: 'Connection' }))) {
        return;
      }
      MessageUtil.error({
        summary: LanguageUtil.get({ id: 'common.label.connection' }),
        detail: (
          <div>
            <b>{connection.name}</b> {LanguageUtil.get({ id: 'messages.cannotdeployed', defaultMessage: 'can not deployed' })}
          </div>
        ),
      });
    }
  }

  public async bulkAction(ids: string[], type: ActionType): Promise<BulkActionDependencyError[]> {
    const logs: any = await this.connectionClient.bulkAction(ids, type);
    return logs;
  }

  public async undeployConnection(connection: CommonConnectionModel) {
    try {
      await this.connectionClient.undeploy(connection.name);
      MessageUtil.success({
        summary: 'Connection',
        detail: (
          <div>
            <b>{connection.name}</b> {LanguageUtil.get({ id: 'messages.undeploy', defaultMessage: 'undeployed' })}
          </div>
        ),
      });
    } catch (e) {
      if (DeployErrorUtil.show(e, LanguageUtil.get({ id: 'common.label.connection', defaultMessage: 'Connection' }))) {
        return;
      }
      MessageUtil.error({
        summary: 'Connection',
        detail: (
          <div>
            <b>{connection.name}</b> {LanguageUtil.get({ id: 'messages.cannotUndeployed', defaultMessage: 'can not undeployed' })}
          </div>
        ),
      });
    }
  }

  private connectionIsValid(connection: CommonConnectionModel) {
    if (connection.name && connection.databaseType && connection.name.trim().length > 0) {
      switch (connection.databaseType) {
        case 'Mongo':
          return !!(connection.databaseName && connection.connectionString);
        default:
          return !!(connection.hostname && connection.port > -1 && connection && connection.username && connection.password);
      }
    }
  }
}

export default ConnectionService;
